// CONTRACT:C4-DESIGN-TOKENS.1.0
// COP — A3 LivingSpine (Item 4): the left-rail active-feed ledger + `+Add` Catalog.
// Spec: docs/ux/SHELL-COMPOSITION.md §2.3.
//
// Two faces of ONE rail surface, flipped IN PLACE:
//
//  (rest)  a vertical, legible ledger of the ACTIVE FEW feeds. Each host-generated
//          row carries: a subscribe/visibility EYE toggle, an honest live COUNT
//          (visible-only — the host already computed it over pdp.visibleEnvelopes()),
//          a HEALTH glyph (ok / stale / down), an OPACITY slider, a ZOOM-TO action,
//          and a REMOVE action. `+Add` (key `A`) flips the rail.
//
//  (flip)  the search-first, role-grouped Catalog (status.catalog) — the PDP-filtered
//          universe. Pick a leaf → handle.addLayer(id) renders it as a new spine row,
//          and the rail flips back. A leaf the PDP withholds is simply ABSENT from the
//          Catalog (Glomar) — never a greyed/locked row.
//
// R-LAW-3: the Catalog flip is the LEFT-RAIL HEAVY PANEL. It registers via
// `useHeavyPanel("catalog")`, so summoning the Throughline dropdown / Ledger graph /
// SettingsDrawer collapses it (and vice-versa). The resting ledger is a LIGHT surface
// and never claims. `Esc` pops exactly one level via `useEscapeStack` (flip → back).
//
// React stays OUT of the render loop: this component only reads the immutable
// CopStatus snapshot and calls imperative CopHandle methods. No host/MapLibre import.

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { CopStatus, CopHandle } from "../host/cop.ts";
import type { LayerRow, CatalogGroup } from "../host/shellContract.ts";
import { useHeavyPanel } from "./heavyPanel.ts";
import { useEscapeStack } from "./useEscapeStack.ts";
import "./spine.css";

export interface LivingSpineProps {
  status: CopStatus;
  handle: CopHandle;
}

/** Subsequence fuzzy match (mirrors the Throughline/cmdk matcher): every char of
 *  `query` appears in `text` in order. Lower score = tighter, earlier match; null =
 *  no match. Case-insensitive. */
function fuzzyScore(query: string, text: string): number | null {
  if (query === "") return 0;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  let ti = 0;
  let score = 0;
  let lastHit = -1;
  for (let qi = 0; qi < q.length; qi++) {
    const found = t.indexOf(q[qi], ti);
    if (found === -1) return null;
    if (lastHit !== -1) score += found - lastHit - 1;
    if (qi === 0) score += found;
    lastHit = found;
    ti = found + 1;
  }
  return score;
}

/** The honest health glyph for a spine row. Shape-primary (not color-only): a filled
 *  diamond = live/ok, a hollow diamond = stale, an open cross = down. Color is a
 *  secondary reinforcement via a data-attr in CSS. Title carries the word. */
function healthGlyph(health: LayerRow["health"]): string {
  switch (health) {
    case "ok":
      return "◆";
    case "stale":
      return "◇";
    case "down":
      return "✕";
  }
}

function healthWord(health: LayerRow["health"]): string {
  switch (health) {
    case "ok":
      return "live";
    case "stale":
      return "stale";
    case "down":
      return "down";
  }
}

// ── one ledger row ───────────────────────────────────────────────────────────

function SpineRow({
  row,
  handle,
}: {
  row: LayerRow;
  handle: CopHandle;
}): JSX.Element {
  const sliderId = useId();
  const pct = Math.round(row.opacity * 100);

  return (
    <li className="spine__row" data-hidden={!row.visible} data-health={row.health}>
      <div className="spine__row-main">
        <button
          type="button"
          className="spine__eye"
          aria-pressed={row.visible}
          title={row.visible ? "Hide feed" : "Show feed"}
          aria-label={`${row.visible ? "Hide" : "Show"} ${row.label}`}
          onClick={() => handle.setLayerVisible(row.id, !row.visible)}
        >
          {row.visible ? "◉" : "◯"}
        </button>

        <span className="spine__label" title={row.label}>
          {row.label}
        </span>

        <span
          className="spine__health"
          data-health={row.health}
          title={`feed ${healthWord(row.health)}`}
          aria-label={`feed ${healthWord(row.health)}`}
        >
          {healthGlyph(row.health)}
        </span>

        <span className="spine__count" title={`${row.count} on the map`}>
          {row.count}
        </span>
      </div>

      <div className="spine__row-controls">
        <input
          id={sliderId}
          className="spine__opacity"
          type="range"
          min={0}
          max={100}
          step={1}
          value={pct}
          aria-label={`${row.label} opacity`}
          title={`Opacity ${pct}%`}
          onChange={(e) => handle.setLayerOpacity(row.id, Number(e.target.value) / 100)}
        />
        <button
          type="button"
          className="spine__act"
          title="Zoom to feed"
          aria-label={`Zoom to ${row.label}`}
          onClick={() => handle.zoomToLayer(row.id)}
        >
          ⤢
        </button>
        <button
          type="button"
          className="spine__act spine__act--remove"
          title="Remove feed"
          aria-label={`Remove ${row.label}`}
          onClick={() => handle.removeLayer(row.id)}
        >
          ✕
        </button>
      </div>
    </li>
  );
}

// ── the Catalog flip (HEAVY surface, R-LAW-3) ─────────────────────────────────

function CatalogFlip({
  catalog,
  onAdd,
  onClose,
}: {
  catalog: CatalogGroup[];
  onAdd: (leafId: string) => void;
  onClose: () => void;
}): JSX.Element {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listboxId = useId();

  // Search-first: focus the input the instant the flip mounts.
  useEffect(() => {
    const id = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, []);

  // Filter each role group by the query; drop groups that go empty. A withheld leaf
  // is already absent from `catalog` (Glomar) — we only ever narrow what's present.
  const groups = useMemo(() => {
    const q = query.trim();
    return catalog
      .map((g) => {
        if (q === "") return g;
        const leaves = g.leaves
          .map((leaf) => {
            const score = Math.min(
              fuzzyScore(q, leaf.label) ?? Infinity,
              (fuzzyScore(q, g.group) ?? Infinity) + 4,
            );
            return Number.isFinite(score) ? { leaf, score } : null;
          })
          .filter((x): x is { leaf: CatalogGroup["leaves"][number]; score: number } => x !== null)
          .sort((a, b) => a.score - b.score)
          .map((x) => x.leaf);
        return { group: g.group, leaves };
      })
      .filter((g) => g.leaves.length > 0);
  }, [catalog, query]);

  const totalLeaves = groups.reduce((n, g) => n + g.leaves.length, 0);

  return (
    <div className="spine__catalog" role="region" aria-label="Add feed catalog">
      <div className="spine__catalog-head">
        <span className="spine__catalog-eyebrow">Add feed</span>
        <button
          type="button"
          className="spine__catalog-back"
          title="Back to feeds"
          aria-label="Back to feeds"
          onClick={onClose}
        >
          ← Back
        </button>
      </div>

      <div className="spine__search">
        <span className="spine__search-icon" aria-hidden="true">
          ⌕
        </span>
        <input
          ref={inputRef}
          className="spine__search-input"
          type="text"
          placeholder="Search feeds…"
          value={query}
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          role="combobox"
          aria-expanded="true"
          aria-controls={listboxId}
          aria-autocomplete="list"
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {totalLeaves === 0 ? (
        <div className="spine__catalog-empty">No matching feeds</div>
      ) : (
        <div className="spine__catalog-list" id={listboxId} role="listbox" aria-label="Feeds">
          {groups.map((g) => (
            <div className="spine__group" key={g.group}>
              <div className="spine__group-title">{g.group}</div>
              {g.leaves.map((leaf) => (
                <button
                  type="button"
                  key={leaf.id}
                  className="spine__leaf"
                  role="option"
                  aria-selected={false}
                  onClick={() => onAdd(leaf.id)}
                >
                  <span className="spine__leaf-plus" aria-hidden="true">
                    +
                  </span>
                  <span className="spine__leaf-label">{leaf.label}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── the Theaters section (mock-only synthetic-backdrop selector) ──────────────
// The synthetic theaters ARE the mock backdrop, so this section shows ONLY in mock
// mode (feedMode === "mock"). Radio semantics — exactly one theater is active; clicking
// an inactive row swaps it LIVE (handle.setScenario → stop old → clear picture → start new,
// no page reload). The active row is highlighted; clicking it is a no-op.

function TheatersSection({
  scenarios,
  active,
  onSelect,
}: {
  scenarios: Array<{ id: string; label: string }>;
  active: string;
  onSelect: (id: string) => void;
}): JSX.Element {
  return (
    <div className="spine__theaters" role="radiogroup" aria-label="Theater">
      <div className="spine__theaters-head">
        <span className="spine__theaters-eyebrow">Theaters</span>
        <span className="spine__theaters-note" title="Synthetic exercise backdrop">
          EXERCISE
        </span>
      </div>
      <ul className="spine__theaters-list">
        {scenarios.map((s) => {
          const isActive = s.id === active;
          return (
            <li key={s.id}>
              <button
                type="button"
                className="spine__theater"
                role="radio"
                aria-checked={isActive}
                data-active={isActive}
                title={isActive ? `${s.label} (active)` : `Switch to ${s.label}`}
                onClick={() => {
                  if (!isActive) onSelect(s.id);
                }}
              >
                <span className="spine__theater-dot" aria-hidden="true">
                  {isActive ? "◉" : "◯"}
                </span>
                <span className="spine__theater-label">{s.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── the rail ──────────────────────────────────────────────────────────────────

export function LivingSpine({ status, handle }: LivingSpineProps): JSX.Element {
  // The Catalog flip is the left-rail HEAVY panel: claiming it collapses any other
  // heavy surface; being displaced (`isActive` flips false) auto-flips us back.
  const heavy = useHeavyPanel("catalog");
  const flipped = heavy.isActive;

  const openCatalog = useCallback(() => heavy.claim(), [heavy]);
  const closeCatalog = useCallback(() => heavy.release(), [heavy]);

  // `A` summons the Catalog flip (ignored while typing in a field). The flip's own
  // Esc registration (below) closes it; A is the open-only affordance.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "a" && e.key !== "A") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || t?.isContentEditable) return;
      e.preventDefault();
      openCatalog();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openCatalog]);

  // Esc pops exactly one level: the Catalog flip → back to the ledger.
  useEscapeStack(closeCatalog, flipped);

  const addAndReturn = useCallback(
    (leafId: string) => {
      handle.addLayer(leafId);
      closeCatalog(); // flip back to the (now-grown) ledger
    },
    [handle, closeCatalog],
  );

  const rows = status.spine;

  // The selectable theaters (static registry) + the mock-only gate. The theaters ARE the
  // synthetic backdrop, so the selector is meaningful only when feedMode === "mock".
  const scenarios = useMemo(() => handle.scenarios(), [handle]);
  const showTheaters = status.feedMode === "mock" && scenarios.length > 0;

  return (
    <section className="spine" aria-label="Living Spine — active feeds" data-flipped={flipped}>
      {flipped ? (
        <CatalogFlip catalog={status.catalog} onAdd={addAndReturn} onClose={closeCatalog} />
      ) : (
        <>
          {showTheaters && (
            <TheatersSection
              scenarios={scenarios}
              active={status.activeScenario}
              onSelect={handle.setScenario}
            />
          )}
          <div className="spine__head">
            <h2 className="spine__title">Feeds</h2>
            <span className="spine__head-count" aria-label={`${rows.length} active feeds`}>
              {rows.length}
            </span>
          </div>

          {rows.length === 0 ? (
            <div className="spine__empty">No active feeds — press A to add one</div>
          ) : (
            <ul className="spine__list">
              {rows.map((row) => (
                <SpineRow key={row.id} row={row} handle={handle} />
              ))}
            </ul>
          )}

          <button
            type="button"
            className="spine__add"
            title="Add a feed (A)"
            aria-label="Add a feed"
            onClick={openCatalog}
          >
            <span className="spine__add-plus" aria-hidden="true">
              +
            </span>
            <span className="spine__add-label">Add</span>
            <kbd className="spine__add-key" aria-hidden="true">
              A
            </kbd>
          </button>
        </>
      )}
    </section>
  );
}
